import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import api, { errMsg, money, openCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err, Ok, Panel } from "@/components/community/kit";
import { allows, day } from "@/lib/community";
import { DuesLedger } from "@/components/community/DuesLedger";

export const Dues = ({ community }) => {
  const cid = community.id;
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [config, setConfig] = useState(null);
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [fallback, setFallback] = useState("");
  const canManage = allows(community, "money", "full") || community.platform_admin;

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/dues`);
      setData(res);
      setConfig((c) => c || res.dues);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  // Coming back from Stripe: confirm the charge before telling anyone it worked.
  const session = params.get("session_id");
  useEffect(() => {
    if (!session) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data: res } = await api.get(`/public/community-dues/status/${session}`);
        if (res.payment_status === "paid") {
          setNotice(`Payment received — your dues run to ${day(res.covers_to)}.`);
          const next = new URLSearchParams(params);
          next.delete("session_id");
          setParams(next, { replace: true });
          await load();
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 10) setTimeout(tick, 2000);
      else setNotice("That payment is still settling. Refresh in a moment.");
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const pay = async (kind) => {
    setBusy(kind); setError(""); setFallback("");
    try {
      const { data: res } = await api.post(`/communities/${cid}/dues/checkout`,
        { kind, origin_url: window.location.origin });
      openCheckout(res.checkout_url, setFallback);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const connect = async () => {
    setBusy("connect"); setError(""); setFallback("");
    try {
      const { data: res } = await api.post(`/communities/${cid}/dues/connect`);
      openCheckout(res.url, setFallback);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const refreshConnect = async () => {
    setBusy("refresh"); setError("");
    try {
      await api.get(`/communities/${cid}/dues/connect/status`);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const openDashboard = async () => {
    setBusy("dash"); setError(""); setFallback("");
    try {
      const { data: res } = await api.post(`/communities/${cid}/dues/connect/login`);
      openCheckout(res.url, setFallback);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const saveKey = async () => {
    setBusy("key"); setError(""); setNotice("");
    try {
      await api.put(`/communities/${cid}/dues/stripe-key`, { secret_key: key });
      setKey(""); setShowKey(false);
      setNotice("Saved — dues will be charged straight onto your own Stripe account.");
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const saveConfig = async () => {
    setBusy("config"); setError(""); setNotice("");
    try {
      await api.put(`/communities/${cid}/dues/config`, config);
      setNotice("Dues updated.");
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  const { dues, connect: cx, my_status: mine } = data;
  const nothingCharged = !dues.joining_fee && !dues.monthly && !dues.annual;

  return (
    <div data-testid="community-dues" className="space-y-4">
      <Err testId="dues-error">{error}</Err>
      <Ok testId="dues-notice">{notice}</Ok>
      {fallback && (
        <p data-testid="dues-fallback" className="text-sm text-slate-400">
          Your browser blocked the popup —{" "}
          <a href={fallback} target="_blank" rel="noreferrer" className="text-gold underline">
            open Stripe in a new tab
          </a>.
        </p>
      )}

      <Panel testId="dues-mine" title="Your membership"
        sub={dues.note || "LimoLinkX charges the community nothing — every cent goes to its own account."}>
        {nothingCharged ? (
          <Empty testId="dues-none">This community does not charge dues.</Empty>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {mine.current ? (
                <Chip testId="dues-status" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> paid to {day(mine.paid_through)}
                </Chip>
              ) : mine.in_grace ? (
                <Chip testId="dues-status" className="border-amber-500/40 bg-amber-500/10 text-amber-300">
                  lapsed {day(mine.paid_through)} · still inside the {dues.grace_days}-day grace
                </Chip>
              ) : (
                <Chip testId="dues-status" className="border-red-500/40 bg-red-500/10 text-red-300">
                  {mine.paid_through ? `overdue since ${day(mine.paid_through)}` : "not paid yet"}
                </Chip>
              )}
              {dues.joining_fee > 0 && mine.joining_paid && <Chip>joining fee paid</Chip>}
            </div>

            {cx.ready ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {dues.joining_fee > 0 && !mine.joining_paid && (
                  <Button data-testid="dues-pay-joining" onClick={() => pay("joining")}
                    disabled={busy === "joining"}>
                    {busy === "joining" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <CreditCard className="mr-2 h-4 w-4" />}
                    Joining fee {money(dues.joining_fee)}
                  </Button>
                )}
                {dues.monthly > 0 && (
                  <Button data-testid="dues-pay-monthly" variant="outline" onClick={() => pay("monthly")}
                    disabled={busy === "monthly"}>
                    {busy === "monthly" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Pay a month · {money(dues.monthly)}
                  </Button>
                )}
                {dues.annual > 0 && (
                  <Button data-testid="dues-pay-annual" variant="outline" onClick={() => pay("annual")}
                    disabled={busy === "annual"}>
                    {busy === "annual" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Pay a year · {money(dues.annual)}
                  </Button>
                )}
              </div>
            ) : (
              <p data-testid="dues-not-ready" className="mt-4 text-sm text-slate-500">
                Card payments are not switched on yet — the treasurer connects the community's own
                account first. Until then dues are collected in person and recorded here.
              </p>
            )}

            {data.my_payments.length > 0 && (
              <ul data-testid="dues-my-payments" className="mt-5 divide-y divide-ink-600">
                {data.my_payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="text-slate-300">{p.kind_label} · {p.method_label}</span>
                    <span className="font-mono text-xs text-slate-500">
                      {money(p.amount)} · {day(p.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Panel>

      {canManage && (
        <>
          <Panel testId="dues-connect" title="Where the money lands"
            sub="Stripe pays the community directly. LimoLinkX takes no fee and never holds the funds."
            action={
              <div className="flex flex-wrap gap-2">
                <Button data-testid="dues-connect-refresh" size="sm" variant="ghost"
                  onClick={refreshConnect} disabled={busy === "refresh"}>
                  {busy === "refresh" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Re-check"}
                </Button>
                {cx.account_id && cx.mode === "connect" && (
                  <Button data-testid="dues-connect-dashboard" size="sm" variant="outline"
                    onClick={openDashboard} disabled={busy === "dash"}>
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Stripe dashboard
                  </Button>
                )}
              </div>
            }>
            <div className="flex flex-wrap items-center gap-2">
              <Chip testId="dues-connect-status"
                className={cx.ready ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-amber-500/40 bg-amber-500/10 text-amber-300"}>
                {cx.ready ? "ready to take cards"
                  : cx.account_id ? "onboarding not finished" : "not connected"}
              </Chip>
              {cx.mode === "key" && <Chip>own Stripe key</Chip>}
              {cx.test_mode && <Chip>test mode</Chip>}
              {cx.payouts_enabled && <Chip>payouts on</Chip>}
            </div>

            {!cx.ready && (
              <div className="mt-4 space-y-3">
                <Button data-testid="dues-connect-start" onClick={connect} disabled={busy === "connect"}>
                  {busy === "connect" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <ShieldCheck className="mr-2 h-4 w-4" />}
                  {cx.account_id ? "Finish Stripe onboarding" : "Connect the community's Stripe account"}
                </Button>
                <button data-testid="dues-key-toggle" onClick={() => setShowKey((v) => !v)}
                  className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-gold">
                  {showKey ? "Hide" : "We already have our own Stripe account"}
                </button>
                {showKey && (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input data-testid="dues-key-input" type="password" placeholder="sk_live_…"
                      value={key} onChange={(e) => setKey(e.target.value)} />
                    <Button data-testid="dues-key-save" onClick={saveKey}
                      disabled={!key.startsWith("sk_") || busy === "key"}>
                      Save key
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {config && (
            <Panel testId="dues-config" title="What members pay">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[["joining_fee", "Joining fee"], ["monthly", "Monthly"], ["annual", "Annual"]]
                  .map(([field, label]) => (
                    <label key={field} className="text-xs text-slate-500">
                      {label}
                      <Input data-testid={`dues-${field}`} type="number" min="0" step="0.01"
                        className="mt-1" value={config[field]}
                        onChange={(e) => setConfig({ ...config, [field]: Number(e.target.value) })} />
                    </label>
                  ))}
                <label className="text-xs text-slate-500">
                  Grace days
                  <Input data-testid="dues-grace" type="number" min="0" max="180" className="mt-1"
                    value={config.grace_days}
                    onChange={(e) => setConfig({ ...config, grace_days: Number(e.target.value) })} />
                </label>
              </div>
              <Input data-testid="dues-note" className="mt-3" placeholder="A line members see about dues"
                value={config.note || ""}
                onChange={(e) => setConfig({ ...config, note: e.target.value })} />
              <Button data-testid="dues-config-save" className="mt-3" onClick={saveConfig}
                disabled={busy === "config"}>
                {busy === "config" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </Panel>
          )}

          <DuesLedger community={community} onChanged={load} />
        </>
      )}
    </div>
  );
};
